import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { normalizeChannelName } from '@avslack/shared';
import { useCreateChannel } from '../data/channels';
import { ApiRequestError } from '../lib/api';
import { Dialog, useAnimatedClose } from './Dialog';

export function NewChannelDialog({ onClose: closeNow }: { onClose: () => void }) {
  const onClose = useAnimatedClose(closeNow);
  const navigate = useNavigate();
  const createChannel = useCreateChannel();
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  /**
   * ALAPBOL PRIVAT.
   *
   * A nyilvanos csatornat a ceg barmely tagja elolvashatja - tagsag nelkul
   * is. Egy uj csatorna eseten ez ritkan a szandek, es a tevedes iranya
   * szamit: egy feleslegesen privat csatornat barmikor meg lehet nyitni, egy
   * veletlenul nyilvanosat viszont mar elolvastak.
   */
  const [isPrivate, setPrivate] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const normalized = normalizeChannelName(name);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!normalized) return;
    setError(null);
    try {
      const channel = await createChannel.mutateAsync({
        name: normalized,
        kind: isPrivate ? 'private' : 'public',
        ...(topic.trim() ? { topic: topic.trim() } : {}),
      });
      onClose();
      void navigate({ to: '/c/$channelId', params: { channelId: channel.id } });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Nem sikerült létrehozni.');
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Új csatorna"
      description="A csatornák egy témához vagy projekthez tartoznak."
    >
      <form onSubmit={(e) => void submit(e)} className="mt-5">
        <Field label="Név" htmlFor="channel-name">
          <div className="flex items-center gap-1.5 rounded-control border border-border-soft bg-surface px-3 py-2 focus-within:border-accent">
            <span aria-hidden="true" className="text-ink-faint">
              #
            </span>
            <input
              id="channel-name"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="pl. rendelő-budapest"
              className="w-full bg-transparent text-base outline-none placeholder:text-ink-faint"
            />
          </div>
          {normalized && normalized !== name.trim().toLowerCase() && (
            <p className="mt-1.5 text-xs text-ink-muted">
              Így fog kinézni: <strong className="text-ink">#{normalized}</strong>
            </p>
          )}
        </Field>

        <Field label="Téma" htmlFor="channel-topic" optional>
          <input
            id="channel-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Miről szól ez a csatorna?"
            className="w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-base outline-none placeholder:text-ink-faint focus:border-accent"
          />
        </Field>

        <label className="mt-4 flex cursor-pointer items-start gap-2.5 rounded-control border border-border-soft p-3 transition-colors duration-(--duration-quick) hover:bg-surface-hover">
          <input
            type="checkbox"
            checked={isPrivate}
            onChange={(e) => setPrivate(e.target.checked)}
            className="mt-0.5 size-4 accent-[var(--accent)]"
          />
          <span className="text-sm">
            <strong className="font-semibold">Privát csatorna</strong>
            <br />
            <span className="text-ink-muted">
              Csak meghívással látható, és a böngészőben sem jelenik meg.
            </span>
          </span>
        </label>

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-control border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-control px-4 py-2 text-sm font-medium text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={!normalized || createChannel.isPending}
            className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover disabled:opacity-40"
          >
            {createChannel.isPending ? 'Létrehozás…' : 'Létrehozás'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  optional,
  children,
}: {
  label: string;
  htmlFor: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4 first:mt-0">
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-semibold">
        {label}
        {optional && <span className="ml-1.5 font-normal text-ink-faint">nem kötelező</span>}
      </label>
      {children}
    </div>
  );
}
